import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/Button";
import { ArrowRightIcon, MailIcon, UserIcon } from "@/components/ui/icons";
import { PasswordField } from "@/components/ui/PasswordField";
import { TextField } from "@/components/ui/TextField";
import { toFormErrors } from "@/utils/apiErrors";

import { FormAlert } from "./FormAlert";
import { RememberToggle } from "./RememberToggle";
import { useRegister } from "./useSession";

// Mirrors HANDLE_PATTERN in apps/accounts/serializers.py. The server is still
// the authority — this only gives instant feedback while typing.
const HANDLE_PATTERN = /^[A-Za-z0-9_]{3,20}$/;
const HANDLE_RULES = "3–20 characters: letters, numbers and underscores.";
const MIN_PASSWORD_LENGTH = 8;

export function RegisterForm() {
  const register = useRegister();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);

  const serverErrors = register.isError ? toFormErrors(register.error) : null;

  const handleLooksInvalid = username.length > 0 && !HANDLE_PATTERN.test(username);
  const canSubmit =
    HANDLE_PATTERN.test(username) &&
    email.trim().includes("@") &&
    password.length >= MIN_PASSWORD_LENGTH;

  const clearErrors = () => {
    if (register.isError) register.reset();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || register.isPending) return;
    register.mutate({ username, email: email.trim(), password, remember });
  };

  return (
    <form noValidate onSubmit={handleSubmit} className="mt-7 space-y-5">
      {serverErrors?.message ? <FormAlert message={serverErrors.message} /> : null}

      <TextField
        label="Citizen handle"
        name="username"
        autoComplete="username"
        autoFocus
        maxLength={20}
        placeholder="Amaka_VIP"
        leadingIcon={<UserIcon />}
        value={username}
        onChange={(event) => {
          setUsername(event.target.value);
          clearErrors();
        }}
        error={serverErrors?.fields.username ?? (handleLooksInvalid ? HANDLE_RULES : undefined)}
        hint="This is how other players will see you in the city."
      />

      <TextField
        label="Email"
        name="email"
        type="email"
        autoComplete="email"
        inputMode="email"
        placeholder="you@example.com"
        leadingIcon={<MailIcon />}
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
          clearErrors();
        }}
        error={serverErrors?.fields.email}
      />

      <PasswordField
        label="Password"
        name="password"
        autoComplete="new-password"
        placeholder="At least 8 characters"
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
          clearErrors();
        }}
        error={serverErrors?.fields.password}
        hint="At least 8 characters. Avoid common passwords or your handle."
      />

      <RememberToggle checked={remember} onChange={setRemember} />

      <Button
        type="submit"
        className="h-14 w-full text-headline-sm"
        loading={register.isPending}
        disabled={!canSubmit}
        icon={<ArrowRightIcon />}
      >
        {register.isPending ? "Issuing passport…" : "Claim my passport"}
      </Button>
    </form>
  );
}